/**
 * Puente React ⇄ Clapper Arcade (iframe en /clapper-arcade/index.html).
 *
 * - Ante `ca:ready` (el juego lo repite cada 500ms hasta ser atendido) postea
 *   `ca:init` con usuario + contenido efectivo + progreso guardado — idempotente,
 *   así un F5 del iframe re-inicializa solo.
 * - Los `ca:event` se encolan y se mandan a reportProgress con debounce de
 *   1.5s (flush inmediato en `finished` y en pagehide). El merge server-side
 *   es idempotente, así que un reintento nunca duplica puntos.
 */
import { useEffect, useRef } from 'react';
import { trpc } from '@/lib/trpc';
import type { LearningEvent } from '@/../../shared/people-learning';

export function useArcadeBridge(iframeRef: React.RefObject<HTMLIFrameElement | null>) {
  const arcade = trpc.peopleLearning.getMyArcade.useQuery(undefined, {
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
  const utils = trpc.useUtils();
  const report = trpc.peopleLearning.reportProgress.useMutation({
    onSuccess: (res) => {
      // Reconciliar puntos server-side (fuente de verdad)
      const win = iframeRef.current?.contentWindow;
      if (win) win.postMessage({ type: 'ca:refresh-progress', progress: res }, window.location.origin);
      if (res.finishedAt) utils.peopleLearning.getMyArcade.invalidate();
    },
  });

  const dataRef = useRef(arcade.data);
  dataRef.current = arcade.data;
  const queueRef = useRef<LearningEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reportRef = useRef(report.mutate);
  reportRef.current = report.mutate;

  useEffect(() => {
    const flush = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      if (queueRef.current.length === 0) return;
      const events = queueRef.current.splice(0, 50);
      reportRef.current({ events });
    };

    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if (e.source !== iframeRef.current?.contentWindow) return;
      const msg = e.data;
      if (!msg || typeof msg !== 'object') return;

      if (msg.type === 'ca:ready') {
        const data = dataRef.current;
        if (!data) return; // la query sigue cargando; el juego reintenta solo
        iframeRef.current?.contentWindow?.postMessage(
          {
            type: 'ca:init',
            user: { ...data.me, lang: data.progress?.lang ?? 'es' },
            content: data.content,
            savedProgress: data.progress,
            // Imágenes del juego firmadas por el host (correcciones 07/09 §1/§5)
            assetUrls: (data as { assetUrls?: Record<string, string | undefined> }).assetUrls ?? {},
          },
          window.location.origin
        );
      } else if (msg.type === 'ca:event' && msg.event) {
        queueRef.current.push(msg.event as LearningEvent);
        if (msg.event.kind === 'finished') flush();
        else {
          if (timerRef.current) clearTimeout(timerRef.current);
          timerRef.current = setTimeout(flush, 1500);
        }
      }
    };

    const onPageHide = () => flush();
    window.addEventListener('message', onMessage);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);
    return () => {
      flush();
      window.removeEventListener('message', onMessage);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoading: arcade.isLoading, error: arcade.error };
}
